import {
  responseGetSettlements,
  failureGetSettlements,
  requestGetSettlements,
  responseDownloadSettlementsPDF,
  requestDownloadSettlementsPDF,
  failureDownloadSettlementsPDF,
  requestSendSettlementsEmail,
  responseSendSettlementsEmail,
  failureSendSettlementsEmail,
  requestGenerateBulkStore,
  responseGenerateBulkStore,
  failureGenerateBulkStore,
  requestGetInvoiceJobs,
  responseGetInvoiceJobs,
  failureGetInvoiceJobs,
} from 'reactRedux/slices/settlementsSlice/SettlementsSlice';
import requestApi from 'utils/Request';
import { downloadFile } from 'utils/DownloadRequest';
import {
  SETTLEMENTS,
  DOWNLOAD_INVOICE,
  DOWNLOAD_SELLER_INVOICE,
  SEND_INVOICE,
  INVOICE_JOB,
  INVOICE_JOBS,
} from 'utils/StaticApiEndpoints';
import { showErrorAlert, showSuccessAlert } from 'utils/AlertUtils';
import { requestDownloadSettlementPDF } from 'reactRedux/slices/settlementsSlice/SettlementsSlice';
import { responseDownloadSettlementPDF } from 'reactRedux/slices/settlementsSlice/SettlementsSlice';
import { failureDownloadSettlementPDF } from 'reactRedux/slices/settlementsSlice/SettlementsSlice';
import { DOWNLOAD_SETTLEMENT } from 'utils/StaticApiEndpoints';

// ---- Async invoice job helpers (RAT-1579 / RAT-1810) ----

// Builds the enqueue body from the existing modal payload shape (merchantIds is
// a comma-separated string there).
const buildInvoiceJobBody = (operation, payload) => {
  const merchantIds =
    typeof payload?.merchantIds === 'string'
      ? payload.merchantIds
          .split(',')
          .map((id) => parseInt(id.trim(), 10))
          .filter((id) => !Number.isNaN(id))
      : Array.isArray(payload?.merchantIds)
        ? payload.merchantIds
        : [];

  const body = { operation, merchantIds, year: parseInt(payload?.year, 10) };
  if (payload?.quarter) body.quarter = payload.quarter;
  if (payload?.month) body.month = payload.month;
  // RAT-1610: last month of a consecutive-month range; omitted = single month.
  if (payload?.endMonth) body.endMonth = payload.endMonth;
  if (payload?.language) body.language = payload.language;
  return body;
};

// RAT-1810: fire-and-forget enqueue. Returns the queueId immediately without
// polling; the heavy work runs in the background and is tracked in the Invoice
// Jobs tab, so the user is never blocked (critical for multi-month runs).
const enqueueInvoiceJob = async (operation, payload) => {
  const enqueueResponse = await requestApi.post(
    INVOICE_JOB,
    buildInvoiceJobBody(operation, payload),
  );
  const queueId = enqueueResponse?.data?.queueId;
  if (!queueId) {
    throw new Error(enqueueResponse?.message || 'Failed to start invoice job');
  }
  return queueId;
};

export const GetSettelementsData = (payload, callback) => async (dispatch) => {
  try {
    dispatch(requestGetSettlements());
    const { data } = await requestApi.get(`${SETTLEMENTS}`, {
      params: payload,
    });
    dispatch(responseGetSettlements(data));
    if (typeof callback === 'function') {
      callback();
    }
  } catch (error) {
    showErrorAlert(error?.message);
    dispatch(failureGetSettlements(error?.message));
  }
};

///////// Download Settlements PDF (Merchant Invoice) ////////
export const DownloadSettlementsPDF =
  (payload, callback) => async (dispatch) => {
    try {
      dispatch(requestDownloadSettlementsPDF());

      const monthLabel =
        payload?.endMonth && payload?.endMonth !== payload?.month
          ? `M${payload?.month}-M${payload?.endMonth}`
          : `M${payload?.month}`;
      const defaultFilename = `INV-${payload.year}Y${payload?.quarter ? `Q${payload?.quarter}` : monthLabel}-SP-${payload.name}`;

      await downloadFile(
        DOWNLOAD_INVOICE,
        payload,
        defaultFilename,
        'application/pdf',
        '.pdf',
        () => {
          dispatch(responseDownloadSettlementsPDF());
          if (typeof callback === 'function') {
            callback();
          }
        },
        (error) => {
          showErrorAlert(error?.message);
          dispatch(failureDownloadSettlementsPDF(error?.message));
        },
      );
    } catch (error) {
      showErrorAlert(error?.message);
      dispatch(failureDownloadSettlementsPDF(error?.message));
    }
  };

///////// Download Seller Invoice PDF ////////
export const DownloadSellerInvoicePDF =
  (payload, callback, onError) => async (dispatch) => {
    try {
      dispatch(requestDownloadSettlementsPDF());

      const sellerMonthLabel =
        payload?.endMonth && payload?.endMonth !== payload?.month
          ? `M${payload?.month}-M${payload?.endMonth}`
          : `M${payload?.month}`;
      const defaultFilename = `INV-${payload.year}Y${payload?.quarter ? `Q${payload?.quarter}` : sellerMonthLabel}-SP-${payload.name}`;

      await downloadFile(
        DOWNLOAD_SELLER_INVOICE,
        {
          sellerId: payload.sellerId,
          year: payload.year,
          ...(payload?.quarter
            ? { quarter: payload.quarter }
            : {
                month: payload.month,
                // RAT-1610: consecutive-month range end (omit for a single month).
                ...(payload?.endMonth &&
                  payload?.endMonth !== payload?.month && {
                    endMonth: payload.endMonth,
                  }),
              }),
          ...(payload?.language &&
            payload.language !== 'en' && { language: payload.language }),
        },
        defaultFilename,
        'application/pdf',
        '.pdf',
        () => {
          dispatch(responseDownloadSettlementsPDF());
          if (typeof callback === 'function') {
            callback();
          }
        },
        (error) => {
          dispatch(failureDownloadSettlementsPDF(error?.message));
          if (typeof onError === 'function') {
            onError();
          }
          showErrorAlert(error?.message);
        },
      );
    } catch (error) {
      dispatch(failureDownloadSettlementsPDF(error?.message));
      if (typeof onError === 'function') {
        onError();
      }
      showErrorAlert(error?.message);
    }
  };

/////// Generate Bulk Store (async job - RAT-1579 / RAT-1810 fire-and-forget) ////////
export const GenerateBulkStore = (payload, callback) => async (dispatch) => {
  try {
    dispatch(requestGenerateBulkStore());
    await enqueueInvoiceJob('merchant_generate', payload);
    showSuccessAlert(
      'Invoice generation started. Track it under Invoice Jobs.',
    );
    dispatch(responseGenerateBulkStore());
    if (typeof callback === 'function') {
      callback();
    }
  } catch (error) {
    showErrorAlert(
      error?.message || 'An error occurred while starting invoice generation.',
    );
    dispatch(failureGenerateBulkStore(error?.message));
  }
};

///////// Send Settlements Email ////////
export const SendSettlementsEmail = (payload, callback) => async (dispatch) => {
  try {
    dispatch(requestSendSettlementsEmail());
    const response = await requestApi.get(`${SEND_INVOICE}`, {
      params: payload,
    });

    if (response?.statusCode === 200) {
      showSuccessAlert('Email sent successfully!');
      dispatch(responseSendSettlementsEmail());
      if (typeof callback === 'function') {
        callback();
      }
    } else {
      // Handle non-200 responses
      showErrorAlert('Failed to send email. Please try again.');
      dispatch(
        failureSendSettlementsEmail(
          response?.message || 'Failed to send email',
        ),
      );
    }
  } catch (error) {
    showErrorAlert('An error occurred while sending email.');
    dispatch(failureSendSettlementsEmail(error?.message));
  }
};

/////// Send Settlements Email Bulk (async job - RAT-1579 / RAT-1810 fire-and-forget) ////////
export const SendSettlementsEmailBulk =
  (payload, callback) => async (dispatch) => {
    try {
      dispatch(requestSendSettlementsEmail());
      await enqueueInvoiceJob('merchant_email', payload);
      showSuccessAlert(
        'Invoice emailing started. Track it under Invoice Jobs.',
      );
      dispatch(responseSendSettlementsEmail());
      if (typeof callback === 'function') {
        callback();
      }
    } catch (error) {
      showErrorAlert(
        error?.message || 'An error occurred while starting invoice emailing.',
      );
      dispatch(failureSendSettlementsEmail(error?.message));
    }
  };

////// Settlement Module download PDF ////////
export const DownloadSettlementPDF =
  (payload, callback) => async (dispatch) => {
    try {
      dispatch(requestDownloadSettlementPDF());

      const defaultFilename = 'P&L Statement';

      await downloadFile(
        DOWNLOAD_SETTLEMENT,
        payload,
        defaultFilename,
        'application/pdf',
        '.pdf',
        () => {
          dispatch(responseDownloadSettlementPDF());
          if (typeof callback === 'function') {
            callback();
          }
        },
        (error) => {
          showErrorAlert(error?.message);
          dispatch(failureDownloadSettlementPDF(error?.message));
        },
      );
    } catch (error) {
      showErrorAlert(error?.message);
      dispatch(failureDownloadSettlementPDF(error?.message));
    }
  };

/////// Download Settlements PDF Bulk (async job - RAT-1579 / RAT-1810 fire-and-forget) ////////
// Enqueues a download job and returns immediately. The generated PDF/ZIP is
// fetched from the Invoice Jobs tab once the job reaches "Ready".
export const DownloadSettlementsPDFBulk =
  (payload, callback) => async (dispatch) => {
    try {
      dispatch(requestDownloadSettlementsPDF());
      await enqueueInvoiceJob('merchant_download', payload);
      showSuccessAlert(
        'Invoice download started. It will be ready under Invoice Jobs.',
      );
      dispatch(responseDownloadSettlementsPDF());
      if (typeof callback === 'function') {
        callback();
      }
    } catch (error) {
      showErrorAlert(
        error?.message || 'An error occurred while starting invoice download.',
      );
      dispatch(failureDownloadSettlementsPDF(error?.message));
    }
  };

/////// Invoice Jobs list (RAT-1810) ////////
export const GetInvoiceJobs = (params) => async (dispatch) => {
  try {
    dispatch(requestGetInvoiceJobs());
    const { data } = await requestApi.get(INVOICE_JOBS, { params });
    dispatch(responseGetInvoiceJobs(data));
  } catch (error) {
    // No alert here: this runs on an auto-refresh poll, so surfacing every
    // transient failure would spam the user. The error is kept in slice state.
    dispatch(failureGetInvoiceJobs(error?.message));
  }
};

/////// Download a completed invoice job's stored file (RAT-1810) ////////
export const DownloadInvoiceJobFile =
  (queueId, defaultFilename, isZip, callback) => async () => {
    try {
      await downloadFile(
        `${INVOICE_JOB}/${queueId}/download`,
        {},
        defaultFilename,
        isZip ? 'application/zip' : 'application/pdf',
        isZip ? '.zip' : '.pdf',
        () => {
          if (typeof callback === 'function') {
            callback();
          }
        },
        (error) => {
          showErrorAlert(error?.message);
        },
      );
    } catch (error) {
      showErrorAlert(error?.message);
    }
  };
